// src/pages/finance-portal/period-closing/PeriodClosing.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card, CardContent,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, IconButton, Alert, CircularProgress, Skeleton,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Tooltip, Autocomplete, Divider,
    MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import LockClockIcon from '@mui/icons-material/LockClock';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import GppGoodIcon from '@mui/icons-material/GppGood';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function formatPercent(val) {
    if (val === null || val === undefined || isNaN(val)) return '—';
    return `${val >= 0 ? '+' : ''}${val.toFixed(2)}%`;
}

const STATUS_STYLES = {
    OPEN: { bg: '#dcfce7', color: '#166534', label: 'Open' },
    SOFT_CLOSED: { bg: '#fef3c7', color: '#92400e', label: 'Soft Closed' },
    HARD_CLOSED: { bg: '#fed7aa', color: '#9a3412', label: 'Hard Closed' },
    SEALED: { bg: '#fecaca', color: '#991b1b', label: 'Sealed' },
};

export default function PeriodClosing() {
    const [periods, setPeriods] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Action dialog
    const [actionDialog, setActionDialog] = useState({ open: false, type: null, period: null });
    const [actionLoading, setActionLoading] = useState(false);

    // Year-end close
    const [yearEndForm, setYearEndForm] = useState({ fiscal_year: new Date().getFullYear(), retained_earnings_account_id: null });
    const [yearEndResult, setYearEndResult] = useState(null);
    const [yearEndLoading, setYearEndLoading] = useState(false);

    // Comparative report
    const [compForm, setCompForm] = useState({ current_period_id: '', prior_period_id: '' });
    const [compResult, setCompResult] = useState(null);
    const [compLoading, setCompLoading] = useState(false);

    const fetchPeriods = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get('/api/finance/fiscal-periods');
            if (res.data.success) {
                setPeriods(res.data.data);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load fiscal periods');
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchAccounts = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/finance/coa', { params: { flat: true } });
            if (res.data.success) setAccounts(res.data.data);
        } catch (err) { /* silent */ }
    }, []);

    useEffect(() => { fetchPeriods(); fetchAccounts(); }, [fetchPeriods, fetchAccounts]);

    const getAccountLabel = (acct) => acct ? `${acct.account_number} - ${acct.name}` : '';
    const findAccount = (id) => accounts.find(a => a.id === id) || null;

    const getNextAction = (status) => {
        switch (status) {
            case 'OPEN': return { label: 'Soft Close', action: 'close', icon: <LockIcon fontSize="small" /> };
            case 'SOFT_CLOSED': return { label: 'Hard Close', action: 'close', icon: <LockIcon fontSize="small" /> };
            case 'HARD_CLOSED': return { label: 'Seal', action: 'seal', icon: <GppGoodIcon fontSize="small" /> };
            default: return null;
        }
    };

    const canReopen = (status) => status === 'SOFT_CLOSED' || status === 'HARD_CLOSED';

    const openAction = (type, period) => {
        setActionDialog({ open: true, type, period });
    };

    const executeAction = async () => {
        setActionLoading(true);
        const { type, period } = actionDialog;
        try {
            if (type === 'close') {
                await axiosInstance.post(`/api/finance/fiscal-periods/${period.id}/close`, {});
                setSuccess(`Period ${period.name} closed successfully`);
            } else if (type === 'seal') {
                await axiosInstance.post(`/api/finance/fiscal-periods/${period.id}/seal`, {});
                setSuccess(`Period ${period.name} sealed successfully`);
            } else if (type === 'reopen') {
                await axiosInstance.post(`/api/finance/fiscal-periods/${period.id}/reopen`, {});
                setSuccess(`Period ${period.name} reopened successfully`);
            }
            setActionDialog({ open: false, type: null, period: null });
            fetchPeriods();
        } catch (err) {
            setError(err.response?.data?.message || `Failed to ${type} period`);
        } finally {
            setActionLoading(false);
        }
    };

    const handleYearEndClose = async () => {
        setYearEndLoading(true);
        setYearEndResult(null);
        try {
            const res = await axiosInstance.post('/api/finance/fiscal-periods/year-end-close', {
                fiscal_year: yearEndForm.fiscal_year,
                retained_earnings_account_id: yearEndForm.retained_earnings_account_id,
            });
            if (res.data.success) {
                setYearEndResult(res.data.data);
                setSuccess('Year-end close completed');
                fetchPeriods();
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to run year-end close');
        } finally {
            setYearEndLoading(false);
        }
    };

    const handleComparativeReport = async () => {
        setCompLoading(true);
        setCompResult(null);
        try {
            const res = await axiosInstance.get('/api/finance/fiscal-periods/reports/comparative', {
                params: { current_period_id: compForm.current_period_id, prior_period_id: compForm.prior_period_id },
            });
            if (res.data.success) {
                setCompResult(res.data.data);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to generate comparative report');
        } finally {
            setCompLoading(false);
        }
    };

    const actionLabels = { close: 'Close', seal: 'Seal', reopen: 'Reopen' };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <LockClockIcon sx={{ fontSize: 28, color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Period Closing</Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Period close & year-end</Typography>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{success}</Alert>}

                {/* Fiscal Periods Table */}
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', color: '#1e293b', mb: 2 }}>Fiscal Periods</Typography>
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 4 }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Name', 'Start Date', 'End Date', 'Year', 'Quarter', 'Status', 'Actions'].map(h => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3, 4].map(i => <TableRow key={i}><TableCell colSpan={7}><Skeleton height={36} /></TableCell></TableRow>)
                                ) : periods.length === 0 ? (
                                    <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography sx={{ fontFamily, color: '#94a3b8' }}>No fiscal periods found</Typography>
                                    </TableCell></TableRow>
                                ) : periods.map(p => {
                                    const statusStyle = STATUS_STYLES[p.status] || { bg: '#f1f5f9', color: '#64748b', label: p.status };
                                    const nextAction = getNextAction(p.status);
                                    return (
                                        <TableRow key={p.id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>{p.name}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{p.start_date}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{p.end_date}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', fontWeight: 600 }}>{p.fiscal_year}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>Q{p.quarter || '—'}</TableCell>
                                            <TableCell>
                                                <Chip label={statusStyle.label} size="small"
                                                    sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22, bgcolor: statusStyle.bg, color: statusStyle.color }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    {nextAction && (
                                                        <Tooltip title={nextAction.label}>
                                                            <Button size="small" variant="outlined" startIcon={nextAction.icon}
                                                                onClick={() => openAction(nextAction.action, p)}
                                                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '8px', borderColor: ACCENT, color: ACCENT, '&:hover': { borderColor: '#16a34a', bgcolor: `${ACCENT}08` }, fontSize: '0.7rem', py: 0.25, minWidth: 'auto' }}
                                                            >
                                                                {nextAction.label}
                                                            </Button>
                                                        </Tooltip>
                                                    )}
                                                    {p.status === 'HARD_CLOSED' && (
                                                        <Tooltip title="Seal">
                                                            <Button size="small" variant="outlined" startIcon={<GppGoodIcon fontSize="small" />}
                                                                onClick={() => openAction('seal', p)}
                                                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '8px', borderColor: '#ef4444', color: '#ef4444', '&:hover': { borderColor: '#dc2626', bgcolor: '#fef2f2' }, fontSize: '0.7rem', py: 0.25, minWidth: 'auto' }}
                                                            >
                                                                Seal
                                                            </Button>
                                                        </Tooltip>
                                                    )}
                                                    {canReopen(p.status) && (
                                                        <Tooltip title="Reopen">
                                                            <IconButton size="small" onClick={() => openAction('reopen', p)}
                                                                sx={{ color: '#94a3b8', '&:hover': { color: '#3b82f6' } }}
                                                            >
                                                                <LockOpenIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>

                {/* Year-End Close Section */}
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', color: '#1e293b', mb: 2 }}>Year-End Close</Typography>
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 4 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                            <TextField label="Fiscal Year" value={yearEndForm.fiscal_year}
                                onChange={e => setYearEndForm(prev => ({ ...prev, fiscal_year: parseInt(e.target.value) || '' }))}
                                size="small" type="number" sx={{ width: 140 }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }}
                            />
                            <Autocomplete
                                options={accounts}
                                value={findAccount(yearEndForm.retained_earnings_account_id)}
                                onChange={(_, val) => setYearEndForm(prev => ({ ...prev, retained_earnings_account_id: val?.id || null }))}
                                getOptionLabel={getAccountLabel}
                                isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                                sx={{ minWidth: 300, flex: 1 }}
                                size="small"
                                renderInput={(params) => (
                                    <TextField {...params} label="Retained Earnings Account" size="small"
                                        InputProps={{ ...params.InputProps, sx: { fontFamily, borderRadius: '10px' } }}
                                        InputLabelProps={{ sx: { fontFamily } }}
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.id}>
                                        <Typography sx={{ fontFamily, fontSize: '0.85rem' }}>{getAccountLabel(option)}</Typography>
                                    </li>
                                )}
                            />
                            <Button variant="contained" startIcon={yearEndLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <PlayArrowIcon />}
                                onClick={handleYearEndClose} disabled={yearEndLoading || !yearEndForm.fiscal_year || !yearEndForm.retained_earnings_account_id}
                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' }, height: 40 }}
                            >
                                Run Year-End Close
                            </Button>
                        </Box>

                        {yearEndResult && (
                            <Box sx={{ mt: 3, p: 2.5, bgcolor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', color: '#1e293b', mb: 1.5 }}>Year-End Close Results</Typography>
                                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                    <Box>
                                        <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>Total Revenue</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '1.2rem', fontWeight: 700, color: ACCENT }}>{formatCurrency(yearEndResult.total_revenue)}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>Total Expenses</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '1.2rem', fontWeight: 700, color: '#ef4444' }}>{formatCurrency(yearEndResult.total_expenses)}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>Net Income</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '1.2rem', fontWeight: 700, color: (yearEndResult.net_income || 0) >= 0 ? ACCENT : '#ef4444' }}>
                                            {formatCurrency(yearEndResult.net_income)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </CardContent>
                </Card>

                {/* Comparative Report Section */}
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', color: '#1e293b', mb: 2 }}>Comparative Report</Typography>
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap', mb: compResult ? 3 : 0 }}>
                            <FormControl size="small" sx={{ minWidth: 220 }}>
                                <InputLabel sx={{ fontFamily }}>Current Period</InputLabel>
                                <Select value={compForm.current_period_id}
                                    onChange={e => setCompForm(prev => ({ ...prev, current_period_id: e.target.value }))}
                                    label="Current Period" sx={{ fontFamily, borderRadius: '10px' }}
                                >
                                    {periods.map(p => (
                                        <MenuItem key={p.id} value={p.id} sx={{ fontFamily }}>{p.name} ({p.fiscal_year})</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <CompareArrowsIcon sx={{ color: '#94a3b8', mt: 1 }} />
                            <FormControl size="small" sx={{ minWidth: 220 }}>
                                <InputLabel sx={{ fontFamily }}>Prior Period</InputLabel>
                                <Select value={compForm.prior_period_id}
                                    onChange={e => setCompForm(prev => ({ ...prev, prior_period_id: e.target.value }))}
                                    label="Prior Period" sx={{ fontFamily, borderRadius: '10px' }}
                                >
                                    {periods.map(p => (
                                        <MenuItem key={p.id} value={p.id} sx={{ fontFamily }}>{p.name} ({p.fiscal_year})</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Button variant="contained" startIcon={compLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <PlayArrowIcon />}
                                onClick={handleComparativeReport} disabled={compLoading || !compForm.current_period_id || !compForm.prior_period_id}
                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' }, height: 40 }}
                            >
                                Generate
                            </Button>
                        </Box>

                        {compResult && compResult.length > 0 && (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                            {['Account #', 'Account Name', 'Current Balance', 'Prior Balance', 'Variance $', 'Variance %'].map(h => (
                                                <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {compResult.map((row, idx) => {
                                            const variance = (row.current_balance || 0) - (row.prior_balance || 0);
                                            const variancePct = row.prior_balance ? (variance / Math.abs(row.prior_balance)) * 100 : null;
                                            const varColor = variance > 0 ? ACCENT : variance < 0 ? '#ef4444' : '#64748b';
                                            return (
                                                <TableRow key={idx} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#64748b' }}>{row.account_number}</TableCell>
                                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#1e293b' }}>{row.account_name}</TableCell>
                                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{formatCurrency(row.current_balance)}</TableCell>
                                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{formatCurrency(row.prior_balance)}</TableCell>
                                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: varColor }}>{formatCurrency(variance)}</TableCell>
                                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: varColor }}>{formatPercent(variancePct)}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {compResult && compResult.length === 0 && (
                            <Typography sx={{ fontFamily, color: '#94a3b8', textAlign: 'center', py: 3 }}>No data for the selected periods</Typography>
                        )}
                    </CardContent>
                </Card>
            </Box>

            {/* Action Confirmation Dialog */}
            <Dialog open={actionDialog.open} onClose={() => setActionDialog({ open: false, type: null, period: null })}
                PaperProps={{ sx: { borderRadius: '16px', fontFamily } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>
                    Confirm {actionLabels[actionDialog.type] || ''} Period
                </DialogTitle>
                <DialogContent>
                    {actionDialog.period && (
                        <Box>
                            <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#475569', mb: 1 }}>
                                Are you sure you want to <strong>{actionDialog.type}</strong> the period <strong>{actionDialog.period.name}</strong>?
                            </Typography>
                            {actionDialog.type === 'seal' && (
                                <Alert severity="warning" sx={{ fontFamily, borderRadius: '10px', fontSize: '0.85rem' }}>
                                    Sealing a period is permanent. No further changes can be made to entries in this period.
                                </Alert>
                            )}
                            {actionDialog.type === 'close' && actionDialog.period.status === 'OPEN' && (
                                <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', mt: 1 }}>
                                    This will soft-close the period. Entries can still be posted with approval.
                                </Typography>
                            )}
                            {actionDialog.type === 'close' && actionDialog.period.status === 'SOFT_CLOSED' && (
                                <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', mt: 1 }}>
                                    This will hard-close the period. No new entries can be posted without reopening.
                                </Typography>
                            )}
                            {actionDialog.type === 'reopen' && (
                                <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', mt: 1 }}>
                                    This will reopen the period, allowing new entries to be posted.
                                </Typography>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setActionDialog({ open: false, type: null, period: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={executeAction} disabled={actionLoading}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            bgcolor: actionDialog.type === 'seal' ? '#ef4444' : ACCENT,
                            '&:hover': { bgcolor: actionDialog.type === 'seal' ? '#dc2626' : '#16a34a' },
                        }}
                    >
                        {actionLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : actionLabels[actionDialog.type] || 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
